import { useEffect, useState } from "react";
import { Alert, AlertDescription } from "../../../../app/components/ui/alert";
import { Button } from "../../../../app/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../../app/components/ui/dialog";
import { Input } from "../../../../app/components/ui/input";

type Props = {
  open: boolean;
  mode: "create" | "edit";
  initialName: string;
  error?: string | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: { name: string }) => Promise<void>;
};

export function BrandFormModal({ open, mode, initialName, error, onOpenChange, onSubmit }: Props) {
  const [name, setName] = useState(initialName);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    setName(initialName);
    setLocalError(null);
  }, [initialName]);

  const title = mode === "create" ? "Add brand" : "Edit brand";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!name.trim()) {
      setLocalError("Name is required.");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({ name: name.trim() });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="brand-name">
              Name
            </label>
            <Input
              id="brand-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Acme"
              autoFocus
            />
          </div>

          {(localError || error) && (
            <Alert variant="destructive">
              <AlertDescription>{localError || error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

